import type {
  IError,
  ProblemDetails,
  ValidationProblemDetails,
  ResponseWithErrors,
} from "entities/common";
import {
  responseWithErrorsSchema,
  problemDetailsSchema,
  validationProblemDetailsSchema,
} from "entities/common";
import axios from "axios";
import { z } from "zod";
import { config } from "@/config/env";
import { getCurrentJWT } from "../tokens/tokenHelpers";
import { firstValue } from "utils/object";
import type { AxiosRequestConfig, AxiosResponse } from "axios";

export const clubhouseApiHeadersV1 = (): {
  headers: {
    authorization: string;
    "Ocp-Apim-Subscription-Key": string;
    "API-Key": string;
    "Content-Type": string;
  };
} => ({
  headers: {
    authorization: `Bearer ${getCurrentJWT()}`,
    "API-Key": config.apiKey,
    "Ocp-Apim-Subscription-Key": config.apimKey,
    "Content-Type": "application/json",
  },
});

export const clubhouseApiHeadersV2 = (): {
  headers: {
    authorization: string;
    "Ocp-Apim-Subscription-Key": string;
    "x-api-version": string;
    "Content-Type": string;
  };
} => ({
  headers: {
    authorization: `Bearer ${getCurrentJWT()}`,
    "Ocp-Apim-Subscription-Key": config.apimKey,
    "x-api-version": "2.0",
    "Content-Type": "application/json",
  },
});

export const clubhouseApiHeadersMultiPart = (): {
  headers: {
    authorization: string;
    "Ocp-Apim-Subscription-Key": string;
    "x-api-version": string;
  };
} => ({
  headers: {
    authorization: `Bearer ${getCurrentJWT()}`,
    "Ocp-Apim-Subscription-Key": config.apimKey,
    "x-api-version": "2.0",
  },
});

export const lockerApiHeaders = (): {
  headers: {
    authorization: string;
    "Ocp-Apim-Subscription-Key": string;
    "x-api-version": string;
    "API-Key": string;
  };
} => ({
  headers: {
    authorization: `Bearer ${getCurrentJWT()}`,
    "Ocp-Apim-Subscription-Key": config.apimKey,
    "x-api-version": "2.0",
    "API-Key": config.apiKeyLocker,
  },
});

export enum ApiResponseErrorType {
  ValidationException = "ValidationException",
  ProblemDetailsException = "ProblemDetailsException",
}

export enum ApiExceptionClassType {
  SCORECARD_NOT_FINALIZED = "error.scorecardnotfinalizedexception",
}

export const isApiStatusSuccess = (status: number | undefined) => {
  return status === 200 || status === 201 || status === 202 ? true : false;
};

export const getApiResponseErrorMessage = (response: any) => {
  if (
    response.type === ApiResponseErrorType.ValidationException ||
    response?.errors
  ) {
    /*return (response as ValidationProblemDetails)?.errors[
      Object.keys(response?.errors)[0]
    ].toString();*/
    return firstValue(
      (response as ValidationProblemDetails)?.errors,
    )?.toString();
  } else {
    return (response as ProblemDetails)?.detail;
  }
};

/**
 * Type guard to check if response data has the result/errors structure
 * (typically used with status 207, but sometimes returned with status 200)
 */
export function isResponseWithErrors<T>(
  data: unknown,
): data is ResponseWithErrors<T> {
  return (
    typeof data === "object" &&
    data !== null &&
    "result" in data &&
    "errors" in data &&
    Array.isArray((data as Record<string, unknown>).errors)
  );
}

export function isProblemDetails(data: unknown): data is ProblemDetails {
  return (
    typeof data === "object" &&
    data !== null &&
    ("type" in data ||
      "title" in data ||
      "status" in data ||
      "detail" in data) &&
    !("result" in data) &&
    !(
      "errors" in data &&
      "title" in data &&
      typeof (data as any).errors === "object"
    )
  );
}

export function isValidationProblemDetails(
  data: unknown,
): data is ValidationProblemDetails {
  return (
    typeof data === "object" &&
    data !== null &&
    "title" in data &&
    "errors" in data &&
    typeof (data as Record<string, unknown>).errors === "object" &&
    !Array.isArray((data as Record<string, unknown>).errors)
  );
}

export function isApiError(
  data: unknown,
): data is ProblemDetails | ValidationProblemDetails {
  return isProblemDetails(data) || isValidationProblemDetails(data);
}

function hasErrorsArray(data: unknown): data is { errors: IError[] } {
  return (
    typeof data === "object" &&
    data !== null &&
    "errors" in data &&
    Array.isArray((data as Record<string, unknown>).errors)
  );
}

export type ApiResult<TResponse> =
  | TResponse
  | ProblemDetails
  | ValidationProblemDetails
  | ResponseWithErrors<TResponse>;
type ResponseParseOptions = {
  successStatuses: number[];
  treatBadRequestAsValidation: boolean;
};

function parseResponse<T>(
  response: AxiosResponse,
  schema: z.ZodType<T>,
  options: ResponseParseOptions,
): ApiResult<T> {
  const { data, status } = response;

  const parseSchema = resolveResponseSchema(schema, data, status, options);
  const parsed = parseSchema.safeParse(data);

  if (!parsed.success) {
    console.warn(`status: ${status} had these errors:`, parsed.error);
    throw new Error("Response did not match any expected schema.");
  }

  // Log errors for non-success responses
  if (
    !options.successStatuses.includes(status) &&
    hasErrorsArray(parsed.data)
  ) {
    parsed.data.errors.forEach((error) => {
      console.warn(error.message);
    });
  }

  return parsed.data;
}

const fetchOptions: ResponseParseOptions = {
  successStatuses: [200],
  treatBadRequestAsValidation: false,
};

export async function fetchWithValidation<T>(
  url: string,
  schema: z.ZodType<T>,
  config?: AxiosRequestConfig,
): Promise<ApiResult<T>> {
  const response = await axios.get(url, config);
  return parseResponse(response, schema, fetchOptions);
}

const deleteOptions: ResponseParseOptions = {
  successStatuses: [200, 207],
  treatBadRequestAsValidation: false,
};

export async function deleteWithValidation(
  url: string,
  config?: any,
): Promise<ApiResult<boolean>> {
  const schema = z.boolean();

  const response = await axios.delete(url, config);
  return parseResponse(response, schema, deleteOptions);
}

const postOptions: ResponseParseOptions = {
  successStatuses: [200, 201],
  treatBadRequestAsValidation: true,
};

const mutateOptions: ResponseParseOptions = {
  successStatuses: [200],
  treatBadRequestAsValidation: true,
};

type HttpMutationMethod = "post" | "patch" | "put";

async function mutateWithValidation<TPayload, TResponse>(
  method: HttpMutationMethod,
  url: string,
  payloadSchema: z.ZodType<TPayload>,
  payload: TPayload,
  responseSchema: z.ZodType<TResponse>,
  options: ResponseParseOptions,
  config?: AxiosRequestConfig,
): Promise<ApiResult<TResponse>> {
  const validatedPayload = payloadSchema.parse(payload);
  const response = await axios[method](url, validatedPayload, config);
  return parseResponse(response, responseSchema, options);
}

export async function postWithValidation<TPayload, TResponse>(
  url: string,
  payloadSchema: z.ZodType<TPayload>,
  payload: TPayload,
  responseSchema: z.ZodType<TResponse>,
  config?: AxiosRequestConfig,
): Promise<ApiResult<TResponse>> {
  return mutateWithValidation(
    "post",
    url,
    payloadSchema,
    payload,
    responseSchema,
    postOptions,
    config,
  );
}

export async function patchWithValidation<TPayload, TResponse>(
  url: string,
  payloadSchema: z.ZodType<TPayload>,
  payload: TPayload,
  responseSchema: z.ZodType<TResponse>,
  config?: AxiosRequestConfig,
): Promise<ApiResult<TResponse>> {
  return mutateWithValidation(
    "patch",
    url,
    payloadSchema,
    payload,
    responseSchema,
    mutateOptions,
    config,
  );
}

export function buildUrl(
  baseUrl: string,
  queryParams: Record<string, any>,
): string {
  const params: string[] = [];

  Object.entries(queryParams).forEach(([key, value]) => {
    if (value !== undefined) {
      if (Array.isArray(value) && value.length > 0) {
        params.push(`${key}=${value.join(",")}`);
      } else if (!Array.isArray(value)) {
        params.push(`${key}=${value}`);
      }
    }
  });

  return params.length > 0 ? `${baseUrl}?${params.join("&")}` : baseUrl;
}

function resolveResponseSchema<T>(
  schema: z.ZodType<T>,
  data: unknown,
  status: number,
  options: ResponseParseOptions,
): z.ZodType<ApiResult<T>> {
  const { successStatuses, treatBadRequestAsValidation } = options;

  // Success responses (200, 201) - check shape first
  if (successStatuses.includes(status)) {
    if (isResponseWithErrors(data)) {
      return responseWithErrorsSchema(schema) as z.ZodType<ApiResult<T>>;
    }
    // Direct response without wrapper
    return schema as z.ZodType<ApiResult<T>>;
  }

  // Multi-status
  if (status === 207) {
    return responseWithErrorsSchema(schema) as z.ZodType<ApiResult<T>>;
  }

  // Bad Request - could be validation errors
  if (status === 400 && treatBadRequestAsValidation) {
    return z.union([
      problemDetailsSchema,
      validationProblemDetailsSchema,
    ]) as z.ZodType<ApiResult<T>>;
  }

  // Other client/server errors
  if (status >= 400 && status < 600) {
    return problemDetailsSchema as z.ZodType<ApiResult<T>>;
  }

  // Fallback
  return schema as z.ZodType<ApiResult<T>>;
}
